/**
 * Configuración del tenant (por subdominio):
 * - `GET /tenant/public/subdomain/:subdomain/info` — marca, contacto, redes
 * - `GET /tenant/public/subdomain/:subdomain/website` — menú (`menu.items`), header, portrait, footer, etc.
 * - `GET /tenant/public/subdomain/:subdomain/vehicles` — listado de autos (ver `fetchTenantVehicles` en publicApi.js)
 * Sin subdominio: `GET /tenant/{uuid}/public/info` con `VITE_TENANT_ID`.
 */

import { mapWebsiteApiToConfig } from './websiteComponentMap'
import { buildTenantTheme } from '../theme/tenantTheme'

const DEFAULT_HEADER_LINKS = [
  { href: '/', labelKey: 'nav.home' },
  { href: '/vehiculos', labelKey: 'nav.buy' },
  { href: '/vehiculos', labelKey: 'nav.rent' },
  { href: '#nosotros', labelKey: 'nav.about' },
  { href: '#agentes', labelKey: 'nav.agents' },
  { href: '#contacto', labelKey: 'nav.contact' },
]

const DEFAULT_FOOTER_LINKS = [
  { href: '/', labelKey: 'nav.home' },
  { href: '/vehiculos', labelKey: 'nav.properties' },
  { href: '#blog', labelKey: 'nav.blog' },
  { href: '#nosotros', labelKey: 'nav.about' },
]

const DEFAULT_CONFIG = {
  tenant: 'default',
  brand: {
    name: 'Automotora',
    logo: null,
    tagline: '',
    description: '',
  },
  contact: {
    phone: '',
    email: '',
    address: '',
    whatsapp: '',
  },
  advisor: null,
  headerNav: DEFAULT_HEADER_LINKS,
  footerNav: DEFAULT_FOOTER_LINKS,
  social: [],
  poweredBy: 'Workit',
  components: {
    header: 'header-horizontal',
    hero: 'hero-centered',
    footer: 'footer-centered-light',
    menu: 'menu-default',
    listings: 'listings-pills',
  },
  faviconImage: null,
  portraitImage: null,
  theme: {
    primary: '#2563eb',
    secondary: '#64748b',
    primaryHover: '#1d4ed8',
  },
  publicInfo: null,
  website: null,
  websiteLayout: null,
}

const MOCK_CONFIG = {
  tenant: 'workit-mock',
  brand: {
    name: 'Probando.',
    tagline: 'Automotora',
    description: 'Workit Automotora: Tu puerta a un nuevo vehículo. Expertos en encontrar el auto perfecto para ti desde 2020.',
  },
  contact: {
    phone: '(809) 222-1111',
    email: 'info@workit.com',
    address: '123 Main St. 11234',
    whatsapp: '',
  },
  advisor: {
    name: 'Asesor de Ventas',
    phone: '+1 829 203 1251',
    email: 'info@workit.com',
    photo: null,
  },
  headerNav: DEFAULT_HEADER_LINKS,
  footerNav: DEFAULT_FOOTER_LINKS,
  social: [
    { name: 'Facebook', url: '#' },
    { name: 'Twitter', url: '#' },
    { name: 'LinkedIn', url: '#' },
    { name: 'Instagram', url: '#' },
  ],
  poweredBy: 'Workit',
  components: {
    header: 'header-horizontal',
    hero: 'hero-centered',
    footer: 'footer-centered-light',
    menu: 'menu-default',
    listings: 'listings-pills',
  },
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'

export const LISTINGS_OPTIONS = ['listings-sidebar', 'listings-pills', 'listings-compact']

const SUBDOMAIN_STORAGE_KEY = 'tenant-web-subdomain'
/** Cache de subdomain resuelto para un Host custom: `{ hostname, subdomain }`. */
const CUSTOM_HOST_SUBDOMAIN_KEY = 'tenant-web-custom-host-subdomain'

function persistSubdomain(subdomain) {
  try {
    sessionStorage.setItem(SUBDOMAIN_STORAGE_KEY, subdomain)
  } catch {
    /* ignore */
  }
}

function readPersistedSubdomain() {
  try {
    const stored = sessionStorage.getItem(SUBDOMAIN_STORAGE_KEY)
    return stored?.trim() || null
  } catch {
    return null
  }
}

function persistCustomHostSubdomain(hostname, subdomain) {
  try {
    sessionStorage.setItem(
      CUSTOM_HOST_SUBDOMAIN_KEY,
      JSON.stringify({
        hostname: String(hostname || '')
          .split(':')[0]
          .toLowerCase(),
        subdomain: String(subdomain || '').trim(),
      }),
    )
  } catch {
    /* ignore */
  }
}

function readCustomHostSubdomain(hostname) {
  try {
    const raw = sessionStorage.getItem(CUSTOM_HOST_SUBDOMAIN_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const host = String(hostname || '')
      .split(':')[0]
      .toLowerCase()
    if (
      parsed &&
      typeof parsed === 'object' &&
      parsed.hostname === host &&
      typeof parsed.subdomain === 'string' &&
      parsed.subdomain.trim()
    ) {
      return parsed.subdomain.trim()
    }
  } catch {
    /* ignore */
  }
  return null
}

function isPlainLocalHost(hostname) {
  const host = String(hostname || '').toLowerCase()
  return host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)
}

/**
 * Extrae el subdominio del host Workit/local, p. ej. `matias` de `matias.workitcars.com`.
 * - `workitcars.com` o `www.workitcars.com` → null
 * - `matias.localhost` → `matias` (útil con Vite)
 * No usar para dominios propios del dealer (www.midominio.com).
 */
export function getSubdomainFromHostname(host) {
  const hostname = String(host || '')
    .split(':')[0]
    .toLowerCase()
  const parts = hostname.split('.').filter(Boolean)
  if (parts.length < 2) return null
  if (hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return null
  }
  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0] === 'www' ? null : parts[0]
  }
  if (parts[0] === 'www') {
    if (parts.length >= 4) return parts[1] || null
    return null
  }
  if (parts.length >= 3) {
    return parts[0] || null
  }
  return null
}

/**
 * Subdominio desde query `?subdomain=matias` (útil en localhost plano).
 * Se persiste en sessionStorage para no perderlo al navegar dentro de la SPA.
 * @returns {string|null}
 */
export function getSubdomainFromSearchParams() {
  if (typeof window === 'undefined' || !window.location?.search) {
    if (typeof window !== 'undefined' && isPlainLocalHost(window.location?.hostname)) {
      return readPersistedSubdomain()
    }
    return null
  }
  const q = new URLSearchParams(window.location.search).get('subdomain')
  const s = q?.trim()
  if (s) {
    persistSubdomain(s)
    return s
  }
  if (isPlainLocalHost(window.location.hostname)) {
    return readPersistedSubdomain()
  }
  return null
}

/** Host de plataforma Workit o entorno local (no dominio propio del dealer). */
export function isWorkitPlatformHostname(hostname) {
  const host = String(hostname || '')
    .split(':')[0]
    .toLowerCase()
  if (!host) return false
  if (host === 'localhost' || host.endsWith('.localhost') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return true
  }
  return host === 'workitcars.com' || host.endsWith('.workitcars.com')
}

/**
 * Subdominio sync (sin llamar al API):
 * 1. Host Workit / localhost → label del host o `?subdomain=`
 * 2. Dominio propio → null (hay que resolver con `resolveSubdomainForTenantFetch`)
 * @returns {string|null}
 */
export function getSubdomainForTenantFetch() {
  if (typeof window === 'undefined' || !window.location) {
    return null
  }
  const host = window.location.hostname
  if (isWorkitPlatformHostname(host)) {
    return getSubdomainFromHostname(host) || getSubdomainFromSearchParams() || null
  }
  // Dominio propio: no inferir del host (evitar tratar "www.midominio.com" como tenant "www").
  return null
}

/**
 * GET /tenant/public/host/:hostname → { subdomain, tenantUuid, status: "ACTIVE" }
 * @param {string} hostname
 * @returns {Promise<{ subdomain: string, tenantUuid: string, status: string, hostname: string }>}
 */
export async function fetchTenantPublicByHost(hostname) {
  const host = String(hostname || '')
    .split(':')[0]
    .trim()
    .toLowerCase()
  if (!host) {
    throw new Error('Hostname inválido')
  }
  const base = getApiBaseUrl()
  if (!base) {
    throw new Error('Falta VITE_API_URL')
  }
  const res = await fetch(`${base}/tenant/public/host/${encodeURIComponent(host)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${res.status}`
    throw new Error(msg)
  }
  if (body.status != null && body.status !== 200 && body.status !== 201) {
    const msg = typeof body.message === 'string' && body.message ? body.message : `Error ${body.status}`
    throw new Error(msg)
  }
  const data = body?.data && typeof body.data === 'object' ? body.data : null
  const subdomain = typeof data?.subdomain === 'string' ? data.subdomain.trim() : ''
  if (!subdomain) {
    throw new Error(`No se pudo resolver el dominio ${host}.`)
  }
  const status = typeof data.status === 'string' ? data.status : 'ACTIVE'
  if (status !== 'ACTIVE') {
    throw new Error(`El dominio ${host} aún no está activo.`)
  }
  return {
    hostname: typeof data.hostname === 'string' ? data.hostname : host,
    subdomain,
    tenantUuid: typeof data.tenantUuid === 'string' ? data.tenantUuid : '',
    status,
  }
}

/**
 * Resuelve el subdominio incluyendo dominio propio:
 * - Host Workit / localhost → lógica sync actual
 * - Otro Host → GET /tenant/public/host/{hostname} y cachea subdomain por host
 * @returns {Promise<string|null>}
 */
export async function resolveSubdomainForTenantFetch() {
  if (typeof window === 'undefined' || !window.location) {
    return null
  }
  const sync = getSubdomainForTenantFetch()
  if (sync) return sync

  const host = window.location.hostname
  if (isWorkitPlatformHostname(host)) {
    return null
  }

  const cached = readCustomHostSubdomain(host)
  if (cached) return cached

  const resolved = await fetchTenantPublicByHost(host)
  persistCustomHostSubdomain(host, resolved.subdomain)
  return resolved.subdomain
}

/**
 * Subdominio para GET .../website (mismo que /info).
 */
export function getWebsiteSubdomainForFetch(infoSubdomain) {
  if (infoSubdomain) return infoSubdomain
  if (typeof window === 'undefined' || !window.location) return null
  const host = window.location.hostname
  if (isWorkitPlatformHostname(host)) {
    return getSubdomainForTenantFetch()
  }
  return readCustomHostSubdomain(host)
}

const API_URL_PROD = 'https://api.workitcars.com'
const API_URL_DEV = 'https://api-dev.workitcars.com'

/**
 * URL base del API. Prioriza VITE_API_URL del build; si falta, infiere por hostname.
 * - `*.workitcars.com` (prod) → api.workitcars.com
 * - localhost / `*.localhost` → api-dev.workitcars.com
 */
export function getApiBaseUrl() {
  const fromEnv = (import.meta.env.VITE_API_URL || '').trim().replace(/\/$/, '')
  if (fromEnv) return fromEnv

  if (typeof window === 'undefined' || !window.location?.hostname) {
    return API_URL_DEV
  }

  const host = window.location.hostname.toLowerCase()

  if (host === 'localhost' || host.endsWith('.localhost') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return API_URL_DEV
  }

  if (host.includes('.dev.') || host.startsWith('dev.') || host.includes('-dev.')) {
    return API_URL_DEV
  }

  if (host === 'workitcars.com' || host.endsWith('.workitcars.com')) {
    return API_URL_PROD
  }

  // Dominio propio del dealer: sin VITE_API_URL asumimos API de producción.
  return API_URL_PROD
}

/**
 * Base URL para archivos en storage (objectKey tipo `tenants/...`).
 * Prioriza `VITE_MEDIA_URL`; si no, deriva del host del API
 * (p. ej. api-dev.workitcars.com → dev-media.workitcars.com).
 */
export function getMediaBaseUrl() {
  const explicit = (import.meta.env.VITE_MEDIA_URL || '').trim().replace(/\/$/, '')
  if (explicit) return explicit

  const api = getApiBaseUrl()
  if (!api) return ''

  try {
    const u = new URL(api)
    const host = u.hostname
    if (host.startsWith('api-dev.')) {
      u.hostname = host.replace(/^api-dev\./, 'dev-media.')
    } else if (host.startsWith('api-dev')) {
      u.hostname = host.replace(/^api-dev/, 'dev-media')
    } else if (host.startsWith('api.')) {
      u.hostname = host.replace(/^api\./, 'media.')
    }
    return u.origin
  } catch {
    return api
  }
}

/**
 * URL absoluta de medios (logo, favicon, fotos de vehículos).
 * Acepta URL completa, ruta relativa al API o objectKey (`tenants/...`).
 */
export function resolveMediaUrl(baseUrl, path) {
  if (path == null || path === '') return null
  const p = String(path).trim()
  if (/^https?:\/\//i.test(p)) return p

  const isObjectKey = p.startsWith('tenants/') || p.startsWith('public/tenants/')
  const base = (isObjectKey ? getMediaBaseUrl() : baseUrl || '').replace(/\/$/, '')
  if (!base) return null

  return p.startsWith('/') ? `${base}${p}` : `${base}/${p}`
}

/** Lee la portada del tenant desde `/info` o `/website` (misma convención que `logoImage`). */
function pickPortraitMediaRaw(data) {
  if (!data || typeof data !== 'object') return null

  const portraitObj = data.portrait != null && typeof data.portrait === 'object' ? data.portrait : null

  return (
    data.portraitImage ??
    data.portrait_image ??
    data.coverImage ??
    data.cover_image ??
    data.heroImage ??
    data.hero_image ??
    data.backgroundImage ??
    data.background_image ??
    data.bannerImage ??
    data.banner_image ??
    portraitObj?.image ??
    portraitObj?.url ??
    portraitObj?.objectKey ??
    portraitObj?.src ??
    data.data?.portraitImage ??
    data.data?.portrait_image ??
    data.data?.coverImage ??
    data.data?.heroImage ??
    data.data?.backgroundImage ??
    null
  )
}

function resolveWebsiteMedia(apiBase, raw) {
  if (raw == null) return null
  if (typeof raw === 'string') return resolveMediaUrl(apiBase, raw)
  if (typeof raw === 'object') {
    const path = raw.url ?? raw.path ?? raw.objectKey ?? raw.fileName ?? raw.src
    return resolveMediaUrl(apiBase, path)
  }
  return null
}

function toSocialUrl(value) {
  const s = String(value).trim()
  if (!s) return '#'
  if (/^https?:\/\//i.test(s) || s.startsWith('//')) return s.startsWith('//') ? `https:${s}` : s
  return `https://${s.replace(/^@/, '')}`
}

/** Normaliza ítems de menú del API a `{ href, label?, labelKey? }`. */
function mapNavItems(items) {
  if (!Array.isArray(items) || items.length === 0) return null
  const mapped = items
    .map((item) => {
      if (item == null) return null
      if (typeof item === 'string') return { href: item, label: item }
      const href = item.href ?? item.url ?? item.path ?? item.link
      if (!href) return null
      return {
        href: String(href),
        label: item.label ?? item.name ?? item.title ?? undefined,
        labelKey: item.labelKey ?? item.translationKey ?? item.key ?? undefined,
      }
    })
    .filter(Boolean)
  return mapped.length > 0 ? mapped : null
}

function pickNav(data, ...keys) {
  for (const key of keys) {
    const nav = mapNavItems(data?.[key])
    if (nav) return nav
  }
  return null
}

function mapComponentsFromApi(data) {
  const raw = data?.components ?? data?.websiteComponents ?? data?.siteComponents
  if (!raw || typeof raw !== 'object') return null
  const out = {}
  const keys = ['header', 'hero', 'footer', 'menu', 'listings']
  for (const k of keys) {
    const v = raw[k] ?? raw[`${k}Type`] ?? raw[`${k}Variant`]
    if (v != null && String(v).trim() !== '') out[k] = String(v).trim()
  }
  return Object.keys(out).length > 0 ? out : null
}

/**
 * Convierte `data` de GET .../public/info al formato interno de la app.
 */
export function mapPublicInfoToConfig(data, apiBaseUrl) {
  const t = data?.tenant
  const name =
    (data?.name && String(data.name).trim()) || (t?.name && String(t.name).trim()) || DEFAULT_CONFIG.brand.name
  const description = (data?.description && String(data.description).trim()) || ''
  const heroTitle =
    (data?.data?.title && String(data.data.title).trim()) ||
    (data?.title && String(data.title).trim()) ||
    ''

  const social = []
  if (data.facebook) social.push({ name: 'Facebook', url: toSocialUrl(data.facebook) })
  if (data.twitter) social.push({ name: 'Twitter', url: toSocialUrl(data.twitter) })
  if (data.linkedin) social.push({ name: 'LinkedIn', url: toSocialUrl(data.linkedin) })
  if (data.instagram) social.push({ name: 'Instagram', url: toSocialUrl(data.instagram) })
  if (data.youtube) social.push({ name: 'YouTube', url: toSocialUrl(data.youtube) })

  const phoneParts = [data.firstPhoneNumber, data.secondPhoneNumber].filter(
    (x) => x != null && String(x).trim() !== '',
  )
  const phone = phoneParts.map((p) => String(p).trim()).join(' · ')
  const wa =
    data.whatsappNumber != null && String(data.whatsappNumber).trim() !== ''
      ? String(data.whatsappNumber).trim()
      : ''

  const headerNav =
    pickNav(data, 'headerNav', 'headerMenu', 'headerNavigation', 'mainMenu') ||
    pickNav(data?.menu, 'header', 'main', 'items') ||
    pickNav(data?.navigation, 'header', 'main')

  const footerNav =
    pickNav(data, 'footerNav', 'footerMenu', 'footerNavigation') ||
    pickNav(data?.menu, 'footer', 'items') ||
    pickNav(data?.navigation, 'footer')

  const componentsFromApi = mapComponentsFromApi(data)
  const tagline = (data.tagline && String(data.tagline).trim()) || ''

  return {
    tenant: t?.uuid || name,
    subdomain: (data.subdomain && String(data.subdomain).trim()) || null,
    brand: {
      name,
      tagline,
      description,
      heroTitle,
      logo: resolveMediaUrl(apiBaseUrl, data.logoImage),
    },
    contact: {
      phone,
      email: (data.email && String(data.email).trim()) || '',
      address: (data.address && String(data.address).trim()) || '',
      whatsapp: wa,
    },
    advisor: data.showAgent ? { name: '', phone, email: '', photo: null } : null,
    headerNav: headerNav ?? undefined,
    footerNav: footerNav ?? undefined,
    components: componentsFromApi ?? undefined,
    social,
    publicInfo: data,
    faviconImage: resolveMediaUrl(apiBaseUrl, data.faviconImage),
    theme: buildTenantTheme(data),
  }
}

/**
 * @param {string} [tenantId] - UUID del tenant solo si no hay subdominio
 * @param {string} [subdomain] - Subdominio explícito → `GET .../subdomain/{subdomain}/info`
 * @returns {Promise<Object>} Configuración del tenant
 */
export async function fetchTenantConfig(tenantId, subdomain) {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 300))
    return mergeWithDefaults({ ...MOCK_CONFIG })
  }

  const apiBase = getApiBaseUrl()

  const bySub =
    (subdomain && String(subdomain).trim()) ||
    getSubdomainForTenantFetch() ||
    (typeof window !== 'undefined' && !isWorkitPlatformHostname(window.location.hostname)
      ? readCustomHostSubdomain(window.location.hostname)
      : null)
  if (bySub) {
    return fetchPublicInfoBySubdomain(bySub, apiBase)
  }

  const id = (tenantId || import.meta.env.VITE_TENANT_ID || '').trim()
  if (!id) {
    console.warn(
      '[TenantConfig] No hay subdominio (host Workit, ?subdomain=, dominio propio activo o VITE_TENANT_ID); usando config mínima.',
    )
    return mergeWithDefaults({})
  }

  return fetchPublicInfoByTenantUuid(id, apiBase)
}

/**
 * @param {string} subdomain
 * @param {string} apiBase
 */
export async function fetchPublicInfoBySubdomain(subdomain, apiBase) {
  const s = String(subdomain || '').trim()
  if (!s) {
    return mergeWithDefaults({})
  }

  const websiteSub = getWebsiteSubdomainForFetch(s) || s
  const infoUrl = `${apiBase}/tenant/public/subdomain/${encodeURIComponent(s)}/info`
  const websiteUrl = `${apiBase}/tenant/public/subdomain/${encodeURIComponent(websiteSub)}/website`

  const [infoJson, websiteData] = await Promise.all([
    fetchPublicInfoJson(infoUrl),
    fetchPublicWebsiteJson(websiteUrl),
  ])

  const infoData = infoJson?.data
  if (infoData == null || typeof infoData !== 'object') {
    throw new Error('Respuesta inválida: falta data en /info')
  }

  return mergeTenantConfigLayers(
    mapPublicInfoToConfig(infoData, apiBase),
    mapWebsiteApiToConfig(websiteData),
    infoData,
    websiteData,
    apiBase,
  )
}

/**
 * @param {string} tenantUuid
 * @param {string} apiBase
 */
export async function fetchPublicInfoByTenantUuid(tenantUuid, apiBase) {
  const id = String(tenantUuid || '').trim()
  if (!id) {
    return mergeWithDefaults({})
  }
  const url = `${apiBase}/tenant/${encodeURIComponent(id)}/public/info`
  const json = await fetchPublicInfoJson(url)
  const data = json?.data
  if (data == null || typeof data !== 'object') {
    throw new Error('Respuesta inválida: falta data')
  }
  return mergeWithDefaults(mapPublicInfoToConfig(data, apiBase))
}

async function fetchPublicInfoJson(url) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const json = await response.json().catch(() => ({}))
  if (!response.ok) {
    const msg = typeof json.message === 'string' && json.message ? json.message : `HTTP ${response.status}`
    throw new Error(msg)
  }
  if (json?.status != null && json.status !== 200) {
    const msg = typeof json.message === 'string' && json.message ? json.message : `Error ${json.status}`
    throw new Error(msg)
  }
  return json
}

/**
 * GET .../website — si no existe el sitio (404), devuelve null sin romper la carga.
 */
async function fetchPublicWebsiteJson(url) {
  const response = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  })
  const json = await response.json().catch(() => ({}))

  if (response.status === 404) {
    console.warn('[TenantConfig] /website no encontrado (404); se usa solo /info.')
    return null
  }

  if (!response.ok) {
    const msg = typeof json.message === 'string' && json.message ? json.message : `HTTP ${response.status}`
    throw new Error(msg)
  }

  if (json?.status != null && json.status !== 200) {
    if (json.status === 404) {
      console.warn('[TenantConfig] /website no encontrado; se usa solo /info.')
      return null
    }
    const msg = typeof json.message === 'string' && json.message ? json.message : `Error ${json.status}`
    throw new Error(msg)
  }

  const data = json?.data
  return data != null && typeof data === 'object' ? data : null
}

function mergeTenantConfigLayers(fromInfo, fromWebsite, infoData, websiteData, apiBase) {
  const brand = {
    ...DEFAULT_CONFIG.brand,
    ...fromInfo?.brand,
    ...fromWebsite?.brand,
  }

  const portraitImage =
    resolveWebsiteMedia(apiBase, pickPortraitMediaRaw(websiteData)) ??
    resolveWebsiteMedia(apiBase, pickPortraitMediaRaw(infoData)) ??
    null

  return mergeWithDefaults({
    ...fromInfo,
    ...fromWebsite,
    brand,
    headerNav:
      websiteData != null
        ? (fromWebsite?.headerNav ?? [])
        : fromWebsite?.headerNav?.length
          ? fromWebsite.headerNav
          : fromInfo?.headerNav,
    footerNav:
      websiteData != null
        ? (fromWebsite?.footerNav ?? [])
        : fromWebsite?.footerNav?.length
          ? fromWebsite.footerNav
          : fromInfo?.footerNav,
    components: {
      ...DEFAULT_CONFIG.components,
      ...fromInfo?.components,
      ...fromWebsite?.components,
    },
    publicInfo: infoData ?? fromInfo?.publicInfo ?? null,
    website: websiteData ?? fromWebsite?.website ?? null,
    websiteLayout: fromWebsite?.websiteLayout ?? null,
    tenant: fromWebsite?.tenant ?? fromInfo?.tenant ?? DEFAULT_CONFIG.tenant,
    subdomain: fromInfo?.subdomain ?? fromWebsite?.subdomain ?? null,
    portraitImage,
  })
}

function mergeWithDefaults(config) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    brand: { ...DEFAULT_CONFIG.brand, ...config?.brand },
    contact: { ...DEFAULT_CONFIG.contact, ...config?.contact },
    advisor: config?.advisor !== undefined ? config.advisor : DEFAULT_CONFIG.advisor,
    headerNav: config?.headerNav !== undefined ? config.headerNav : DEFAULT_CONFIG.headerNav,
    footerNav: config?.footerNav !== undefined ? config.footerNav : DEFAULT_CONFIG.footerNav,
    social: config?.social != null && config.social.length > 0 ? config.social : DEFAULT_CONFIG.social,
    components: { ...DEFAULT_CONFIG.components, ...config?.components },
    faviconImage: config?.faviconImage ?? DEFAULT_CONFIG.faviconImage,
    portraitImage: config?.portraitImage ?? DEFAULT_CONFIG.portraitImage,
    theme: { ...DEFAULT_CONFIG.theme, ...config?.theme },
    publicInfo: config?.publicInfo ?? null,
    website: config?.website ?? null,
    websiteLayout: config?.websiteLayout ?? null,
  }
}

export { DEFAULT_CONFIG }
